// TODO: This component has a lot of business logic and is tailored
// to 2 or 3 specific use cases. It is difficult to change anything here
// or to use it for other things.
import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Flex, Box, Text } from 'rebass/styled-components';
import styled from 'styled-components';
import { fontWeight } from 'styled-system';
import theme from 'styled-theming';
import { FiCopy } from 'react-icons/fi';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { Toggle, TypeIcon, Delete } from '.';

const TIME_TO_SHOW_COPIED = 1500;

const rowColor = theme.variants('mode', 'mode', {
  active: {
    light: (props) => props.theme.colors.deepGrey,
    dark: (props) => props.theme.colors.white,
  },
  enabled: {
    light: (props) => props.theme.colors.lightGrey,
    dark: (props) => props.theme.lpColorsDark.graya9,
  },
  disabled: {
    light: (props) => props.theme.colors.midGrey,
    dark: (props) => props.theme.lpColorsDark.gray6d,
  },
  copied: {
    light: (props) => props.theme.colors.mediumPurple,
    dark: (props) => props.theme.colors.white,
  },
});

const backgroundColor = theme.variants('mode', 'mode', {
  active: {
    light: (props) => props.theme.colors.white,
    dark: (props) => props.theme.colors.lpGhost,
  },
  enabled: {
    light: (props) => props.theme.colors.ghost,
    dark: (props) => props.theme.colors.lpGhostSoft,
  },
  disabled: {
    light: (props) => props.theme.colors.ghostDark,
    dark: (props) => props.theme.colors.white,
  },
});

const hoverColor = theme.variants('mode', 'mode', {
  active: {
    light: (props) => props.theme.colors.greyBlue,
    dark: (props) => props.theme.colors.white,
  },
  enabled: {
    light: (props) => props.theme.colors.deepGrey,
    dark: (props) => props.theme.colors.white,
  },
  disabled: {
    light: (props) => props.theme.colors.midGrey,
    dark: (props) => props.theme.colors.white,
  },
  copied: {
    light: (props) => props.theme.colors.mediumPurple,
    dark: (props) => props.theme.colors.white,
  },
});

const hoverBackground = theme.variants('mode', 'mode', {
  active: {
    light: (props) => props.theme.colors.white,
    dark: (props) => props.theme.colors.lpGhost,
  },
  enabled: {
    light: (props) => props.theme.colors.white,
    dark: (props) => props.theme.colors.lpGhost,
  },
  disabled: {
    light: (props) => props.theme.colors.ghostDark,
    dark: (props) => props.theme.colors.lpGhostSoft,
  },
});

const borderColor = theme.variants('mode', 'mode', {
  active: {
    light: (props) => props.theme.colors.reallyLightPurple,
    dark: (props) => props.theme.colors.mediumPurple,
  },
  enabled: {
    light: (props) => props.theme.colors.ghostDark,
    dark: (props) => props.theme.colors.lpGhost,
  },
  disabled: {
    light: 'transparent',
    dark: 'green',
  },
  copied: {
    light: (props) => props.theme.colors.ghostDark,
    dark: (props) => props.theme.colors.lpGhost,
  },
});

const labelColor = theme('mode', {
  light: (props) => props.theme.colors.lightGrey,
  dark: (props) => props.theme.colors.dimGrey,
});

const successColor = theme('mode', {
  light: (props) => props.theme.colors.success,
  dark: (props) => props.theme.colors.aqua,
});

export const ToggleLabel = styled(Text)`
  color: ${(props) => (props.enabled ? successColor : labelColor)};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledRow = styled(Flex)`
  ${fontWeight}
  transition: ${(props) => props.theme.animation.normal};
  cursor: ${(props) => (props.onClick || props.enableCopy ? 'pointer' : 'default')};
  color: ${rowColor};
  background-color: ${(props) => !props.isListType && (props.onClick ? backgroundColor : props.theme.colors.ghost)};
  border-radius: ${(props) => (props.isListType ? 0 : props.theme.radius.medium)};
  border-color: ${borderColor};
  border-style: solid;
  border-width: ${(props) => {
    if (props.isListType) {
      if (props.isLast) {
        return '0';
      }
      return '0 0 1px 0';
    }
    return '1px';
  }}
  &:hover {
    color: ${hoverColor};
    background-color: ${(props) => !props.isListType && hoverBackground}
  }
`;

export const CopyIcon = styled(FiCopy)`
  stroke-width: 1.5px;
  transition: ${(props) => props.theme.animation.normal};
  color: ${(props) => props.theme.colors.midGrey};
  ${StyledRow}:hover & {
    color: ${(props) => props.theme.colors.lightGrey};
  }
`;

export const CopyIconActive = styled(FiCopy)`
  transition: ${(props) => props.theme.animation.normal};
  color: ${(props) => props.theme.colors.mediumPurple};
  ${StyledRow}:hover & {
    color: ${(props) => props.theme.colors.mediumPurple};
  }
`;

export const Name = styled(Text)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
`;

const Domain = styled(Text)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  opacity: 0.7;
`;

export const CopiedMessage = styled(Box)`
  color: ${(props) => props.theme.colors.midGrey};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 110px;
`;

function Row(props) {
  const {
    data,
    handleChange,
    handleClick,
    handleDelete,
    isActive,
    useIcon,
    rowType,
    isLast,
    enableCopy,
    themeMode,
    disableAutoScroll,
    ...otherProps
  } = props;

  const [copied, setCopied] = useState(false);
  const [isTogglingSwitch, setIsTogglingSwitch] = useState(false);

  const isMounted = useRef(false);

  useEffect(() => {
    isMounted.current = true;
    if (
      isActive
      && !disableAutoScroll
      && isMounted
      && isMounted.current
      && typeof isMounted.current.scrollIntoView === 'function'
    ) {
      isMounted.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
    }
    return () => {
      isMounted.current = false;
    };
  }, []);  // eslint-disable-line

  if (!data) {
    return null;
  }

  const name = data.name || data.botName || data.varName;
  const domain = data.domain || undefined;
  const type = data.type || data.botType || undefined;

  const mode = (copied && 'copied') || (isActive && 'active') || 'enabled';

  const isListType = rowType === 'list';

  const unsaved = data.isTemporary ? (
    <Box color="midGrey" ml={1} flexShrink={0}>
      (unsaved)
    </Box>
  ) : null;

  const rowRender = (
    <StyledRow
      key={data.id}
      mode={mode}
      isActive={isActive}
      isListType={isListType}
      isLast={isLast}
      enableCopy={enableCopy}
      enabled={data.enabled || !handleChange}
      fontSize={isListType ? 2 : 1}
      fontWeight={400}
      alignItems="center"
      pl={3}
      pr={3}
      py={3}
      mb={isListType ? 0 : 2}
      onClick={handleClick ? (() => handleClick(data)) : undefined}
      ref={isMounted}
      data-testid="policy-row"
      {...otherProps}
    >
      {enableCopy && (
        <Box mr={3}>{copied ? <CopyIconActive size={18} /> : <CopyIcon size={18} />}</Box>
      )}
      {useIcon && (data.botType || type) && <TypeIcon type={data.botType || type} />}
      <Flex mr={3} alignItems="center" flex={1}>
        <Name title={name} data-testid="policy-row-name">{name}</Name>
        {domain && <Domain title={domain} ml={3}>{`(${domain})`}</Domain>}
        {copied ? (
          <CopiedMessage fontSize={0} ml={3}>
            copied to clipboard
          </CopiedMessage>
        ) : null}
        {unsaved}
      </Flex>
      {handleDelete && (
        <Delete
          checked={data.enabled}
          onClick={(event) => handleDelete(data, event.target.checked)}
        />
      )}
      {handleChange && (
        <Flex flex={1} justifyContent="flex-end" alignItems="center">
          {handleChange && (
            <ToggleLabel
              mr={2}
              fontSize={1}
              fontWeight={300}
              enabled={data.enabled}
              onClick={(event) => event.stopPropagation()}
            >
              {`${data.enabled ? 'enabled' : 'disabled'}`}
            </ToggleLabel>
          )}
          {handleChange && (
            <Box onClick={(event) => event.stopPropagation()} minWidth={50}>
              <Toggle
                data-testid={`policy-row-toggle${isTogglingSwitch ? '-enabled' : ''}`}
                className={themeMode === 'light' ? null : 'dark-theme'}
                icons={false}
                checked={data.enabled}
                onChange={(event) => {
                  handleChange(data,
                    event.target.checked,
                    setIsTogglingSwitch,
                    !!isMounted.current);
                }}
              />
            </Box>
          )}
        </Flex>
      )}
    </StyledRow>
  );

  return enableCopy ? (
    <CopyToClipboard
      text={name}
      onCopy={() => {
        if (isMounted.current) {
          setCopied(true);
        }
        setTimeout(() => {
          if (isMounted.current) {
            setCopied(false);
          }
        }, TIME_TO_SHOW_COPIED);
      }}
    >
      {rowRender}
    </CopyToClipboard>
  ) : (
    rowRender
  );
}

Row.defaultProps = {
  enableCopy: false,
  isLast: false,
  data: undefined,
  rowType: undefined,
  isActive: false,
  useIcon: false,
  handleClick: undefined,
  handleChange: undefined,
  handleDelete: undefined,
  disableAutoScroll: false,
};

Row.propTypes = {
  themeMode: PropTypes.string.isRequired,
  enableCopy: PropTypes.bool,
  isLast: PropTypes.bool,
  rowType: PropTypes.string,
  data: PropTypes.object,
  handleClick: PropTypes.func,
  handleDelete: PropTypes.func,
  handleChange: PropTypes.func,
  isActive: PropTypes.bool,
  useIcon: PropTypes.bool,
  disableAutoScroll: PropTypes.bool,
};

export default Row;
